import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// 테마 선택(브라우저 테마·햇빛·달빛)을 모든 페이지가 함께 쓰고, 새로고침·다시 방문해도 기억한다 (CLAUDE.md Phase 22)
// 전에는 페이지마다 따로 'auto' 로 시작해, 홈에서 달빛으로 바꾼 뒤 Resume 로 가면 브라우저 테마(라이트 운영체제면 햇빛)로 돌아갔다
const STORAGE_KEY = 'webPortfolio.colorTheme';
const THEMES = ['auto', 'light', 'dark'];
const DARK_QUERY = '(prefers-color-scheme: dark)';

const readStoredTheme = () => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(value) ? value : 'auto';
  } catch {
    return 'auto'; // 저장소를 못 쓰면(사생활 보호 모드 등) 이번 방문 동안만 기억한다
  }
};

// 첫 화면부터 맞는 테마가 되게 렌더 전에 운영체제 테마를 바로 읽는다(전엔 false 로 시작해 다크 운영체제에서 햇빛이 잠깐 비쳤다)
const readSystemDark = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia(DARK_QUERY).matches
  : false);

const ColorThemeContext = createContext(null);

export function ColorThemeProvider({ children }) {
  const [colorTheme, setColorTheme] = useState(readStoredTheme);
  const [systemDarkMode, setSystemDarkMode] = useState(readSystemDark);

  // 운영체제 테마가 바뀌면 따라간다(브라우저 테마 상태일 때 화면에 반영). 옛 브라우저는 addListener
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setSystemDarkMode(query.matches);
    onChange();
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else query.removeListener(onChange);
    };
  }, []);

  // 고른 테마를 기억한다
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, colorTheme);
    } catch {
      /* 저장소를 못 써도 동작에는 지장 없다 */
    }
  }, [colorTheme]);

  const value = useMemo(() => ({
    colorTheme,
    systemDarkMode,
    currentTheme: colorTheme === 'auto' ? (systemDarkMode ? 'dark' : 'light') : colorTheme,
    // 순환: 브라우저 테마 → 햇빛 → 달빛 → 브라우저 테마
    toggleColorTheme: () => setColorTheme((theme) => (theme === 'auto' ? 'light' : theme === 'light' ? 'dark' : 'auto')),
  }), [colorTheme, systemDarkMode]);

  return <ColorThemeContext.Provider value={value}>{children}</ColorThemeContext.Provider>;
}

export function useColorTheme() {
  const value = useContext(ColorThemeContext);
  if (!value) throw new Error('useColorTheme must be used inside <ColorThemeProvider>');
  return value;
}
